import { GeneralTooltip } from '@components/common/GeneralTooltip';
import MaterialIcon from '@components/common/MaterialIcon';
import CheckboxTogglerT from '@components/tailwind-components/Input/CheckboxTogglerT';
import { InputT } from '@components/tailwind-components/Input/InputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import { LLMUsageCheckResultStatus } from '@conversed/rest/src/contracts/llm-usages/check-results';
import type { TReportIssueParams } from '@conversed/rest/src/contracts/llm-usages/report-issue';
import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { usePermissions } from '@hooks/usePermissions';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationReviewReportIssue } from './ConversationReviewReportIssue';

type Props = {
  questionNumber: number;
  status: LLMUsageCheckResultStatus | '';
  isDisabled: boolean;
  isPriorityKnowledge: boolean;
  useForAIKnowledgeData: boolean;
  reportIssue: boolean;
  reportIssueParams: TReportIssueParams;
  saveAnswerAsTemplate: boolean;
  templateName: string;
  onIsPriorityKnowledgeChange: (value: boolean) => void;
  onUseForAIKnowledgeDataChange: (value: boolean) => void;
  onReportIssueChange: (value: boolean) => void;
  onReportIssueParamsChange: (params: TReportIssueParams) => void;
  onSaveAnswerAsTemplateChange: (value: boolean) => void;
  onTemplateNameChange: (value: string) => void;
};

export const ConversationReviewAdditionalActionsDrawer: React.FC<Props> = ({
  questionNumber,
  status,
  isDisabled,
  isPriorityKnowledge,
  useForAIKnowledgeData,
  reportIssue,
  reportIssueParams,
  saveAnswerAsTemplate,
  templateName,
  onIsPriorityKnowledgeChange,
  onUseForAIKnowledgeDataChange,
  onReportIssueChange,
  onReportIssueParamsChange,
  onSaveAnswerAsTemplateChange,
  onTemplateNameChange,
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);

  const isCorrection = status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT;

  // Status-dependent gates per the four secondary controls.
  const canSaveAsTemplate =
    isCorrection &&
    hasPermissions([PERMISSIONS.TEMPLATE_RESPONSE.TEMPLATE_RESPONSE_CREATE]);
  // Priority Knowledge is saved on the review record, so it requires a status first; it
  // also only routes a training document when the answer feeds the dataset.
  const isPriorityDisabled = isDisabled || !status || !useForAIKnowledgeData;
  const isUseForAIDisabled =
    isDisabled || status === LLMUsageCheckResultStatus.IRRELEVANT;

  // Badge counts every currently-active secondary action.
  const activeActionCount = [
    isPriorityKnowledge,
    reportIssue && isCorrection,
    saveAnswerAsTemplate && canSaveAsTemplate,
    useForAIKnowledgeData,
  ].filter(Boolean).length;

  return (
    <div
      className={`conversation-review-modal__drawer${
        isOpen ? ' conversation-review-modal__drawer--open' : ''
      }`}>
      {/* Always-visible handle on the modal's right edge */}
      <button
        type='button'
        className='conversation-review-modal__drawer-handle'
        onClick={() => setIsOpen((previous) => !previous)}>
        {activeActionCount > 0 && (
          <span className='conversation-review-modal__drawer-badge'>
            {activeActionCount}
          </span>
        )}
        <MaterialIcon
          icon='bolt'
          classes={['conversation-review-modal__drawer-handle-icon']}
        />
        <span className='conversation-review-modal__drawer-handle-label'>
          {t('train-ai.conversation-review.additional-actions.title')}
        </span>
      </button>

      <div className='conversation-review-modal__drawer-panel'>
        <div className='conversation-review-modal__drawer-header'>
          <div className='conversation-review-modal__drawer-header-title'>
            <MaterialIcon
              icon='bolt'
              type='outlined'
              classes={['conversation-review-modal__drawer-header-icon']}
            />
            <span className='conversation-review-modal__drawer-header-title-text'>
              {t('train-ai.conversation-review.additional-actions.title')}
            </span>
            <span className='conversation-review-modal__drawer-header-question'>
              {' - '}
              {t(
                'train-ai.conversation-review.additional-actions.question-label',
                { number: questionNumber },
              )}
            </span>
          </div>
          <button
            type='button'
            className='conversation-review-modal__drawer-close'
            onClick={() => setIsOpen(false)}>
            <MaterialIcon
              icon='chevron_right'
              classes={['!leading-none', '!text-lg']}
            />
          </button>
        </div>

        <div className='conversation-review-modal__drawer-body'>
          {/* 1. Add to Priority Knowledge (status-independent; gated by useForAIKnowledgeData) */}
          <div
            className={`conversation-review-modal__priority${
              isPriorityDisabled
                ? ' conversation-review-modal__priority--disabled'
                : ''
            }`}>
            <label
              htmlFor='add-to-priority-knowledge'
              className='conversation-review-modal__priority-row'>
              <input
                id='add-to-priority-knowledge'
                type='checkbox'
                className='conversation-review-modal__priority-checkbox'
                checked={isPriorityKnowledge}
                disabled={isPriorityDisabled}
                onChange={(event) =>
                  onIsPriorityKnowledgeChange(event.target.checked)
                }
              />
              <span className='conversation-review-modal__priority-crown'>
                <MaterialIcon
                  icon='workspace_premium'
                  classes={['conversation-review-modal__priority-crown-icon']}
                />
              </span>
              <span className='conversation-review-modal__priority-label'>
                {t(
                  'train-ai.conversation-review.additional-actions.priority-knowledge.label',
                )}
              </span>
              <GeneralTooltip
                text={t(
                  'train-ai.conversation-review.additional-actions.priority-knowledge.tooltip',
                )}
                placement='left'
                className='conversation-review-modal__priority-tooltip'>
                <MaterialIcon
                  type='outlined'
                  icon='info'
                  classes={['conversation-review-modal__priority-info']}
                />
              </GeneralTooltip>
            </label>
            {isPriorityDisabled && (
              <span className='conversation-review-modal__drawer-note'>
                {' - '}
                {t(
                  'train-ai.conversation-review.additional-actions.priority-knowledge.disabled-note',
                )}
              </span>
            )}
          </div>

          {/* 2. Report issue - only shown while a correction is being written */}
          {isCorrection && (
            <ConversationReviewReportIssue
              reportIssue={reportIssue}
              reportIssueParams={reportIssueParams}
              disabled={isDisabled}
              onReportIssueChange={onReportIssueChange}
              onReportIssueParamsChange={onReportIssueParamsChange}
            />
          )}

          {/* 3. Save as template - only shown while a correction is being written */}
          {isCorrection && (
            <div className='conversation-review-modal__drawer-item'>
              <CheckboxTogglerT
                id='save-as-template-toggle'
                label={t('train-ai.fact-checking.save-as-template')}
                labelClassName='text-gray-700 !text-tiny'
                colorScheme='default'
                toggleSize='xs'
                checked={saveAnswerAsTemplate && canSaveAsTemplate}
                options={{ disabled: isDisabled || !canSaveAsTemplate }}
                onChange={(event) => {
                  onSaveAnswerAsTemplateChange(event.target.checked);
                  // Clear the name when toggled off so a stale value isn't resubmitted later.
                  if (!event.target.checked) {
                    onTemplateNameChange('');
                  }
                }}
              />
              {saveAnswerAsTemplate && canSaveAsTemplate && (
                <div className='conversation-review-modal__drawer-template-name'>
                  <LabelT
                    text={t('modals.save-as-template.name-input')}
                    className='!text-tiny'
                  />
                  <InputT
                    type='text'
                    options={{
                      value: templateName,
                      onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                        onTemplateNameChange(event.target.value),
                    }}
                    placeholder={t('modals.save-as-template.name-input')}
                    maxLength={100}
                    className='!text-tiny'
                    parentClassName='!mb-0'
                  />
                </div>
              )}
            </div>
          )}

          {/* 4. Use for A.I. knowledge data (status-independent; disabled for irrelevant) */}
          <div className='conversation-review-modal__drawer-item'>
            <CheckboxTogglerT
              id='use-for-ai-knowledge-data-toggle'
              label={t('train-ai.fact-checking.use-for-ai-knowledge-data')}
              labelClassName='text-gray-700 !text-tiny'
              colorScheme='default'
              toggleSize='xs'
              checked={useForAIKnowledgeData}
              options={{ disabled: isUseForAIDisabled }}
              onChange={(event) =>
                onUseForAIKnowledgeDataChange(event.target.checked)
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
